import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { LiveIndicator } from '@sorare/core/src/atoms/ui/LiveIndicator';
import ResponsiveImg from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_PLAY_EVENTTYPE_MY_LINEUPS_LINEUP } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/gql';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { isFixtureLive } from 'lib/so5';

import {
  PastLeaderboardListQuery,
  PastLeaderboardListQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  padding-top: var(--double-unit);
  border-top: 1px solid var(--c-neutral-400);
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  &.isNotCounted {
    opacity: 0.5;
  }
`;

const VerticalBottom = styled(Vertical)`
  margin-top: auto;
  text-align: right;
`;

const OverallScore = styled(LabelS)`
  &.isNotCounted {
    text-decoration: line-through;
  }
`;

const PAST_LEADERBOARD_LIST_QUERY = gql`
  query PastLeaderboardListQuery($userSlug: String!, $userGroupSlug: String!) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        topScoresCount
        membership(userSlug: $userSlug) {
          id
          ... on So5UserGroupMembership {
            id
            lastSo5Rankings {
              id
              score
              ranking
              so5Fixture {
                slug
                cutOffDate
                shortDisplayName
                type
                ...isFixtureLive_so5Fixture
              }
              so5Leaderboard {
                slug
              }
              so5Lineup {
                id
                so5Appearances {
                  id
                  score
                  anyCard {
                    slug
                    pictureUrl
                  }
                  anyPlayer {
                    slug
                    displayName
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${isFixtureLive.fragments.so5Fixture}
` as TypedDocumentNode<
  PastLeaderboardListQuery,
  PastLeaderboardListQueryVariables
>;

type Props = {
  userSlug: string;
  expanded?: boolean;
  userGroupSlug: string;
  activeLineupId?: string;
};

export const PastLeaderboardList = ({
  userSlug,
  expanded,
  userGroupSlug,
  activeLineupId,
}: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const { data } = useQuery(PAST_LEADERBOARD_LIST_QUERY, {
    skip: !expanded,
    variables: { userSlug, userGroupSlug },
  });

  if (!isType(data?.so5.so5UserGroup.membership, 'So5UserGroupMembership')) {
    return null;
  }

  const lastSo5Rankings = data?.so5.so5UserGroup.membership?.lastSo5Rankings;
  if (!lastSo5Rankings) return null;

  const bestRankings = Object.values(lastSo5Rankings)
    .sort((a, b) => (b?.score || -1) - (a?.score || -1))
    .slice(0, data.so5.so5UserGroup.topScoresCount || 0);

  return (
    <Wrapper gap={2}>
      {lastSo5Rankings.map(so5Ranking => {
        if (!so5Ranking) return null;
        const lineupId = so5Ranking.so5Lineup?.id;
        const isFromBestRankings = bestRankings.includes(so5Ranking);

        return (
          <Vertical key={so5Ranking.id}>
            <Link
              replace
              to={generateSportContextPath(
                ANY_SPORT_PLAY_EVENTTYPE_MY_LINEUPS_LINEUP,
                {
                  sport,
                  params: {
                    eventType: so5Ranking.so5Fixture.type.toLowerCase(),
                    id: idFromObject(lineupId),
                    lineup: idFromObject(lineupId),
                    fixture: so5Ranking.so5Fixture.slug,
                  },
                }
              )}
            >
              <Horizontal gap={1}>
                <LabelS
                  bold
                  color={
                    activeLineupId === lineupId
                      ? 'var(--c-brand-300)'
                      : 'var(--c-neutral-600)'
                  }
                >
                  <FormattedDate
                    value={so5Ranking.so5Fixture.cutOffDate}
                    month="short"
                    day="2-digit"
                  />
                </LabelS>
                {isFixtureLive(so5Ranking.so5Fixture) && <LiveIndicator />}
              </Horizontal>
            </Link>
            <StyledSBHorizontal
              className={classNames({ isNotCounted: !isFromBestRankings })}
            >
              <Horizontal>
                {so5Ranking.so5Lineup.so5Appearances.map(appearance => {
                  return (
                    <Vertical center key={appearance.id} gap={0.5}>
                      <ResponsiveImg
                        width={26}
                        cropWidth={40}
                        alt={appearance.anyPlayer.displayName}
                        src={appearance.anyCard?.pictureUrl || ''}
                      />
                      <LabelS>
                        <FormattedNumber
                          value={appearance.score}
                          maximumFractionDigits={0}
                        />
                      </LabelS>
                    </Vertical>
                  );
                })}
              </Horizontal>
              <VerticalBottom>
                <BodyS>
                  <FormattedMessage
                    {...glossary.ordinalStyled}
                    values={{
                      ordinal: so5Ranking.ranking,
                      sup: (...chunks) => <LabelXS as="sup">{chunks}</LabelXS>,
                    }}
                  />
                </BodyS>
                <LabelXS>
                  <Horizontal center gap={0.5} as="span">
                    <FormattedMessage
                      {...fantasy.pointsWithStyledScore}
                      values={{
                        score: so5Ranking.score,
                        span: (...chunks) => (
                          <OverallScore
                            bold
                            as="span"
                            className={classNames({
                              isNotCounted: !isFromBestRankings,
                            })}
                          >
                            {chunks}
                          </OverallScore>
                        ),
                      }}
                    />
                  </Horizontal>
                </LabelXS>
              </VerticalBottom>
            </StyledSBHorizontal>
          </Vertical>
        );
      })}
    </Wrapper>
  );
};
